import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'motion/react';
import type { Place } from '../types/Place';
import { getCountryEmoji } from '../utils/countryEmoji';
import { PhotoGallery } from './PhotoGallery';
import { CloseIcon } from './icons';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { useTimeoutRefs } from '../hooks/useTimeoutRef';
import {
  DURATION_DRAMATIC,
  DURATION_SLOW,
  DURATION_NORMAL,
} from '../utils/animationTiming';

type PopupStage = 'hidden' | 'hook' | 'location' | 'photo' | 'complete';

// Use shared animation tokens for consistency
const STAGE_TIMINGS = {
  hook: DURATION_DRAMATIC,      // 500ms - Story reveals
  location: DURATION_SLOW,      // 300ms - Standard reveal
  photo: DURATION_SLOW,         // 300ms - Standard reveal
  closeButton: DURATION_NORMAL, // 200ms - Default transitions
};

interface PlacePopupProps {
  place: Place;
  isOpen: boolean;
  onClose: () => void;
  onShowInJournal?: () => void;
  // Story 6.1: Gallery props for multi-photo places
  galleryIndex?: number;
  onGalleryIndexChange?: (index: number) => void;
  // Story 6.4: Fullscreen photo view props
  isFullscreen?: boolean;
  onFullscreenChange?: (isFullscreen: boolean) => void;
  // SPRINT-4.6: Edit place callback
  onEdit?: (place: Place) => void;
}

export function PlacePopup({
  place,
  isOpen,
  onClose,
  onShowInJournal,
  galleryIndex = 0,
  onGalleryIndexChange,
  isFullscreen = false,
  onFullscreenChange,
  onEdit,
}: PlacePopupProps) {
  const [currentStage, setCurrentStage] = useState<PopupStage>('hidden');
  const [pushTimer, clearTimers] = useTimeoutRefs();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && currentStage === 'complete') {
        onClose();
      }
    },
    [onClose, currentStage]
  );

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget && currentStage === 'complete') {
        onClose();
      }
    },
    [onClose, currentStage]
  );

  const handleSkipStages = useCallback(() => {
    if (currentStage !== 'complete' && currentStage !== 'hidden') {
      clearTimers();
      setCurrentStage('complete');
    }
  }, [currentStage, clearTimers]);

  const handleContentKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter') {
        handleSkipStages();
      }
    },
    [handleSkipStages]
  );

  // Stage progression effect
  useEffect(() => {
    if (!isOpen) {
      clearTimers();
      setCurrentStage('hidden');
      return;
    }

    // Start staged reveal
    setCurrentStage('hook');

    // Progress through stages
    pushTimer(() => setCurrentStage('location'), STAGE_TIMINGS.hook);
    pushTimer(
      () => setCurrentStage('photo'),
      STAGE_TIMINGS.hook + STAGE_TIMINGS.location
    );
    pushTimer(
      () => setCurrentStage('complete'),
      STAGE_TIMINGS.hook + STAGE_TIMINGS.location + STAGE_TIMINGS.photo
    );

    return clearTimers;
  }, [isOpen, clearTimers, pushTimer]);

  // Keyboard event listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Focus close button when complete
  useEffect(() => {
    if (currentStage === 'complete') {
      closeButtonRef.current?.focus();
    }
  }, [currentStage]);

  // Focus trap when popup is complete
  useFocusTrap(dialogRef, currentStage === 'complete');

  if (!isOpen) {
    return null;
  }

  const isStageReached = (stage: PopupStage): boolean => {
    const stages: PopupStage[] = ['hidden', 'hook', 'location', 'photo', 'complete'];
    return stages.indexOf(currentStage) >= stages.indexOf(stage);
  };

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center transition-opacity duration-normal"
      onClick={handleBackdropClick}
    >
      <div
        className="absolute inset-0 bg-black/70 cursor-pointer"
        aria-hidden="true"
        onClick={handleBackdropClick}
        onTouchEnd={(e) => {
          if (currentStage === 'complete') {
            e.preventDefault();
            onClose();
          }
        }}
      />

      <div
        ref={dialogRef}
        className="relative z-10 w-[90%] md:w-[70%] max-w-[400px] bg-bg-surface rounded-2xl p-6 cursor-pointer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="popup-title"
        onClick={handleSkipStages}
        onKeyDown={handleContentKeyDown}
        tabIndex={0}
      >
        {/* Close Button - appears after complete stage */}
        <motion.button
          ref={closeButtonRef}
          initial={{ opacity: 0 }}
          animate={{ opacity: currentStage === 'complete' ? 1 : 0 }}
          transition={{ duration: STAGE_TIMINGS.closeButton / 1000 }}
          onClick={(e) => {
            e.stopPropagation();
            if (currentStage === 'complete') {
              onClose();
            }
          }}
          disabled={currentStage !== 'complete'}
          className="absolute top-2 right-2 text-gray-400 hover:text-white transition-colors duration-fast min-w-11 min-h-11 w-11 h-11 flex items-center justify-center z-10 disabled:pointer-events-none"
          aria-label="Close popup"
        >
          <CloseIcon />
        </motion.button>

        {/* Stage 1: Hook Phrase */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={
            isStageReached('hook')
              ? { opacity: 1, y: 0 }
              : { opacity: 0, y: 20 }
          }
          transition={{ duration: STAGE_TIMINGS.hook / 1000, ease: 'easeOut' }}
          className="text-xl text-primary font-light tracking-wide mb-4"
        >
          "{place.hook}"
        </motion.p>

        {/* Stage 2: Location & Date */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={
            isStageReached('location')
              ? { opacity: 1, x: 0 }
              : { opacity: 0, x: -20 }
          }
          transition={{
            duration: STAGE_TIMINGS.location / 1000,
            ease: 'easeOut',
          }}
          className="text-sm text-gray-400 flex items-center gap-2 mb-4"
        >
          <span>{getCountryEmoji(place.country)}</span>
          <span id="popup-title">{place.location}</span>
          <span>•</span>
          <span>{place.dateDisplay ?? place.date}</span>
        </motion.div>

        {/* Stage 3: Photo Gallery - Story 6.2: Use PhotoGallery component */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={
            isStageReached('photo')
              ? { opacity: 1, scale: 1 }
              : { opacity: 0, scale: 0.9 }
          }
          transition={{ duration: STAGE_TIMINGS.photo / 1000, ease: 'easeOut' }}
          onClick={(e) => e.stopPropagation()}
        >
          <PhotoGallery
            photos={place.photos}
            currentIndex={galleryIndex}
            onIndexChange={onGalleryIndexChange || (() => {})}
            className="w-full"
            isFullscreen={isFullscreen}
            onFullscreenChange={onFullscreenChange}
          />
        </motion.div>

        {/* Action buttons - appear after complete stage */}
        {(onShowInJournal || onEdit) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: currentStage === 'complete' ? 1 : 0 }}
            transition={{ duration: STAGE_TIMINGS.closeButton / 1000 }}
            className="flex items-center gap-4 mt-4"
          >
            {onShowInJournal && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (currentStage === 'complete') {
                    onShowInJournal();
                  }
                }}
                disabled={currentStage !== 'complete'}
                className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors min-h-[44px] disabled:pointer-events-none"
                aria-label={`View ${place.location} in Journal`}
                data-testid="show-in-journal-button"
              >
                <span>View in Journal</span>
              </button>
            )}
            {onEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (currentStage === 'complete') {
                    onEdit(place);
                  }
                }}
                disabled={currentStage !== 'complete'}
                className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors min-h-[44px] disabled:pointer-events-none"
                aria-label={`Edit ${place.location}`}
                data-testid="edit-place-button"
              >
                <span>Edit</span>
              </button>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
